import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sqHomeScreenStore, sqLicenseManagementStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { setShowModal } from '@/core/hooks/useModalManager.hook';
import { ReleaseNotesModal } from '@/header/ReleaseNotesModal';
import { HelpCenterModal } from '@/helpCenter/HelpCenterModal';
import { CopyBranchCode } from '@/homescreen/CopyBranchCode.atom';
import { LabelAndURL } from '@/homescreen/LabelAndURL.atom';
import { TrainingPromptModal } from '@/homescreen/TrainingPromptModal.molecule';
import { setTrainingPromptDisplay } from '@/homescreen/homescreen.actions';
import { TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import { LICENSE_STATUS } from '@/licenseManagement/licenseManagement.constants.ts';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import {
  cancelAllServerRequests as cancelAllServerRequestsUtils,
  cancelCurrentUserServerRequests as cancelCurrentUserServerRequestsUtils,
} from '@/requests/pendingRequests.utilities';
import {
  canReadAuditTrail,
  hasAnalyticsAdminCapability,
  isAdmin,
  showAdministrationMenuItem,
} from '@/services/authorization.service';
import { emitAllServerRequestsCanceled } from '@/services/notifier.service';
import {
  isLearnModeEnabled,
  isLockingAndVersioningEnabled,
  isNewHomeScreen2025Enabled,
  releaseNotesEnabled,
} from '@/services/systemConfiguration.utilities';
import { doTrack } from '@/track/track.service';
import { dismissHelpCenterBadge, getHelpCenterBadgeInitialState } from '@/utilities/helpCenter.utilities';
import { successToast, warnToast } from '@/utilities/toast.utilities';
import { Icon, TriggerWithDropdown } from '@seeqdev/qomponents';

export const HamburgerMenu = () => {
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showHelpCenterBadge, setShowHelpCenterBadge] = useState(getHelpCenterBadgeInitialState);
  const trainingPromptDisplay: TrainingPromptDisplay = useFluxPath(
    sqHomeScreenStore,
    () => sqHomeScreenStore.trainingPromptDisplay,
  );
  const branchCode = useFluxPath(sqHomeScreenStore, () => sqHomeScreenStore.branchCode);
  const showOfficeHours = useFluxPath(sqLicenseManagementStore, () => sqLicenseManagementStore.showOfficeHours);
  const showTrainingModal = trainingPromptDisplay === TrainingPromptDisplay.MODAL;

  const { t } = useTranslation();
  const licenseStatus = useFluxPath(sqLicenseManagementStore, () => sqLicenseManagementStore.licenseStatus);
  const accessibleWithCurrentLicense = !_.includes(
    [LICENSE_STATUS.NONE, LICENSE_STATUS.UNKNOWN, LICENSE_STATUS.EXPIRED],
    licenseStatus,
  );

  const cancelCurrentUserServerRequests = () =>
    cancelCurrentUserServerRequestsUtils()
      .then(() => {
        successToast({ messageKey: 'REQUEST_CANCELLATION.SUCCESS_USER' });
      })
      .catch((error) => {
        warnToast({ messageKey: 'REQUEST_CANCELLATION.FAILURE' });
        return error;
      });

  const cancelAllServerRequests = () =>
    cancelAllServerRequestsUtils()
      .then(emitAllServerRequestsCanceled)
      .then(() => {
        successToast({ messageKey: 'REQUEST_CANCELLATION.SUCCESS_ALL' });
      })
      .catch((error) => {
        warnToast({ messageKey: 'REQUEST_CANCELLATION.FAILURE' });
        return error;
      });

  const openAdminTools = () => {
    doTrack('Menu', 'Administration', 'open');
    window.open('/administration', 'SeeqAdmin');
  };

  const openApiReference = () => {
    doTrack('Menu', 'API Reference', 'open');
    const swaggerUrl = `/api-docs/?baseUrl=${window.encodeURIComponent(`${APPSERVER_API_PREFIX}/swagger.json`)}`;
    window.open(swaggerUrl);
  };

  const handleHelpCenterClick = () => {
    doTrack('Menu', 'Help Center', 'open');
    setShowModal(HelpCenterModal, true);

    if (showHelpCenterBadge) {
      dismissHelpCenterBadge();
      setShowHelpCenterBadge(false);
    }
  };

  const generateInformationalEntry = (element: React.ReactNode) => ({
    key: `custom_${Date.now()}`,
    isLabel: true,
    display: true,
    customRender: element,
  });

  const generateDropdownEntry = ({
    iconClass = '',
    translationKey = '',
    action = _.noop,
    divider = false,
    testId = '',
    badge = undefined as string | undefined,
  }) => {
    const label = badge ? (
      <>
        {t(translationKey)}
        <span className="ml7 new-badge">{t(badge)}</span>
      </>
    ) : (
      t(translationKey)
    );

    return {
      key: `dropdown_${translationKey}`,
      icon: iconClass,
      itemExtraClassNames: 'sq-force-text-gray forceFont',
      labelTestId: `moreActions_${translationKey}`,
      iconTestId: `moreActionsIcon_${translationKey}`,
      onClick: action,
      label,
      hasDivider: divider,
      testId,
    };
  };

  const TrainingSteps = (
    <ol className="mb5 font-size-smaller" data-testid="training-steps-list">
      {!isLearnModeEnabled() && (
        <li>
          {branchCode ? (
            <CopyBranchCode text="HOME_SCREEN.TRAINING.BRANCH_CODE" branchCode={branchCode} />
          ) : (
            t('HOME_SCREEN.TRAINING.ASK_ADMIN_BRANCH_CODE')
          )}
        </li>
      )}
      <li>
        <LabelAndURL
          label={isLearnModeEnabled() ? 'HOME_SCREEN.TRAINING.GO_TO' : 'HOME_SCREEN.TRAINING.REGISTER_ON'}
          href="https://training.seeq.com"
          website="training.seeq.com"
        />
      </li>
    </ol>
  );

  const actions = _.compact([
    {
      iconClass: 'fa-question-circle',
      translationKey: 'HELP_CENTER.TITLE',
      action: handleHelpCenterClick,
      display: accessibleWithCurrentLicense,
      testId: 'supportRequest',
      divider: !releaseNotesEnabled(),
      badge: showHelpCenterBadge ? 'HOME_SCREEN.NEW' : undefined,
    },
    releaseNotesEnabled()
      ? {
          href: '#',
          iconClass: 'fa-memo-circle-info',
          translationKey: 'RELEASE_NOTES.TITLE',
          action: () => {
            setShowReleaseNotes(true);
            doTrack('Menu', 'Release Notes', 'open');
          },
          display: accessibleWithCurrentLicense,
          testId: 'releaseNotes',
          divider: true,
        }
      : null,
    {
      iconClass: 'fc-training',
      translationKey: 'HOME_SCREEN.TRAINING_CERTIFICATION',
      action: () => {
        doTrack('Menu', 'Training Certification', 'open');
        if (isLearnModeEnabled()) {
          window.open('https://training.seeq.com');
        } else if (isNewHomeScreen2025Enabled()) {
          setTrainingPromptDisplay(TrainingPromptDisplay.HOME_SCREEN);
        } else {
          window.open('https://telemetry.seeq.com/training');
        }
      },
      display: true,
      divider: !isNewHomeScreen2025Enabled() && !showOfficeHours,
      testId: 'trainingCertification',
    },
    isNewHomeScreen2025Enabled() ? generateInformationalEntry(TrainingSteps) : null,
    {
      iconClass: 'fc-support',
      translationKey: 'HOME_SCREEN.OFFICE_HOURS',
      action: () => {
        doTrack('Menu', 'Office Hours', 'open');
        window.open('https://telemetry.seeq.com/office-hours');
      },
      display: showOfficeHours,
      testId: 'officeHours',
      divider: true,
    },
    {
      iconClass: 'fa-lock',
      translationKey: 'ADMINISTRATION',
      action: openAdminTools,
      display: showAdministrationMenuItem() && accessibleWithCurrentLicense,
      testId: 'administration',
    },
    {
      iconClass: 'fa-file-zipper',
      translationKey: 'LICENSE.LICENSE_MANAGEMENT',
      display: isAdmin(),
      testId: 'licenseManagement',
      action: () => {
        doTrack('Menu', 'License Management', 'open');
        window.open('/license');
      },
    },
    {
      iconClass: 'fa-file-lines',
      translationKey: 'LOGS.VIEW_LOGS',
      display: accessibleWithCurrentLicense,
      testId: 'viewLogs',
      action: () => {
        doTrack('Menu', 'View Logs', 'open');
        window.open('/logs');
      },
      divider: true,
    },
    {
      iconClass: 'fc-version-log',
      translationKey: 'ACCESS_CONTROL.LOCKING.VERSIONING.VERSION_LOG',
      display: isLockingAndVersioningEnabled(),
      testId: 'versionLog',
      action: () => {
        doTrack('Menu', 'Version Log', 'open');
        window.open('/version-log', 'SeeqVersionLog');
      },
    },
    {
      iconClass: 'fas fa-th-list',
      translationKey: 'AUDIT_TRAIL.VIEW_ENTRIES',
      display: canReadAuditTrail(),
      testId: 'viewEntries',
      action: () => {
        doTrack('Menu', 'Audit Trail', 'open');
        window.open('/auditTrail');
      },
    },
    {
      iconClass: 'fa-code',
      translationKey: 'API_REFERENCE',
      action: openApiReference,
      display: isAdmin(),
      testId: 'apiReference',
      divider: true,
    },
    {
      iconClass: 'fa-user-xmark',
      translationKey: 'REQUEST_CANCELLATION.CANCEL_USER',
      action: () => {
        doTrack('Menu', 'Cancel User Requests', 'click');
        cancelCurrentUserServerRequests();
      },
      display: true,
      testId: 'requestCancellationUser',
    },
    {
      iconClass: 'fa-ban',
      translationKey: 'REQUEST_CANCELLATION.CANCEL_ALL',
      action: () => {
        doTrack('Menu', 'Cancel All Requests', 'click');
        cancelAllServerRequests();
      },
      display: hasAnalyticsAdminCapability(),
      testId: 'requestCancellationAll',
    },
  ]);

  return (
    <>
      <TriggerWithDropdown
        id="hamburger-menu"
        keepFocusInsideDropdown={false}
        triggerIcon={
          <Icon
            icon="fa-bars"
            extraClassNames="fa-xlg sq-navbar-default flexNoGrowNoShrink pr12"
            type="white"
            testId="hamburgerMenu"
            id="options"
          />
        }
        dropdownItems={_.filter(actions, (action) => action.display).map((action) => {
          if ('customRender' in action) {
            return { ...action, label: action.customRender, onClick: _.noop };
          }
          return generateDropdownEntry(action);
        })}
      />
      {showReleaseNotes && <ReleaseNotesModal onClose={() => setShowReleaseNotes(false)} />}
      {showTrainingModal && (
        <TrainingPromptModal onClose={() => setTrainingPromptDisplay(TrainingPromptDisplay.NONE)} />
      )}
    </>
  );
};
